import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Filter, Shield, AlertTriangle, MapPin, ChevronUp, X } from "lucide-react";
import { useStore } from "@/store/store";
import { useCallback } from "react";
import { useTheme } from "@/components/theme-provider";

const ZONE_TYPES = [
  { 
    type: "Prohibited", 
    label: "Prohibited", 
    icon: Shield, 
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/20",
    borderColor: "border-red-200 dark:border-red-800"
  },
  { 
    type: "Restricted", 
    label: "Restricted", 
    icon: AlertTriangle, 
    color: "text-red-700 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/20",
    borderColor: "border-red-200 dark:border-red-800"
  },
  { 
    type: "Danger", 
    label: "Danger", 
    icon: AlertTriangle, 
    color: "text-orange-700 dark:text-orange-400",
    bgColor: "bg-orange-100 dark:bg-orange-900/20",
    borderColor: "border-orange-200 dark:border-orange-800"
  }
];

export const ZoneFilter: React.FC = () => {
  const { 
    zoneTypeFilter, 
    setZoneTypeFilter,
    minAltitude,
    maxAltitude,
    setMinAltitude,
    setMaxAltitude
  } = useStore();
  const shouldShowFlightRestrictions = useStore(useCallback((state) => state.shouldShowFlightRestrictions, []));
  const setShouldShowFlightRestrictions = useStore(useCallback((state) => state.setShouldShowFlightRestrictions, []));
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleTypeToggle = (type: string) => {
    // Auto-enable flight restrictions when user interacts with filters
    if (!shouldShowFlightRestrictions) {
      setShouldShowFlightRestrictions(true);
    }
    
    if (zoneTypeFilter.includes(type)) {
      setZoneTypeFilter(zoneTypeFilter.filter(t => t !== type));
    } else {
      setZoneTypeFilter([...zoneTypeFilter, type]);
    }
  };

  const handleSelectAll = () => {
    // Auto-enable flight restrictions when user interacts with filters
    if (!shouldShowFlightRestrictions) {
      setShouldShowFlightRestrictions(true);
    }
    setZoneTypeFilter(["Prohibited", "Restricted", "Danger"]);
  };

  const handleSelectNone = () => {
    // Auto-enable flight restrictions when user interacts with filters
    if (!shouldShowFlightRestrictions) {
      setShouldShowFlightRestrictions(true);
    }
    setZoneTypeFilter([]);
  };

  const handleClearAltitude = () => {
    setMinAltitude(null);
    setMaxAltitude(null);
  };

  const hasActiveAltitudeFilter = minAltitude !== null || maxAltitude !== null;

  const selectedCount = zoneTypeFilter.length;
  const totalCount = ZONE_TYPES.length;

  return (
    <Card
      className={`border transition-all duration-200 ${
        isOpen ? "w-80" : "w-12 p-0 py-2"
      } ${
        theme === "dark" 
          ? "bg-gray-900/95 text-white border-gray-700" 
          : "bg-white/95 text-black border-gray-200"
      }`}
    >
      {!isOpen ? (
        <div 
          className="flex items-center justify-center p-0 cursor-pointer hover:bg-gray-800/20 dark:hover:bg-gray-100/20 transition-colors relative group"
          onClick={() => setIsOpen(true)}
          title={`Zone Filter - ${selectedCount}/${totalCount} selected`}
        >
          <Filter className="h-5 w-5" />
          {selectedCount < totalCount && (
            <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {selectedCount}
            </div>
          )}
        </div>
      ) : (
        <>
          <div 
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-800/20 dark:hover:bg-gray-100/20 transition-colors"
            onClick={() => setIsOpen(false)}
          >
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <h3 className="font-medium text-sm">Zone Filter</h3>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {selectedCount}/{totalCount}
              </Badge>
              <ChevronUp className="h-4 w-4" />
            </div>
          </div>
          <div className="px-3 pb-3 space-y-4">
        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            className="flex-1"
          >
            All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectNone}
            className="flex-1"
          >
            None
          </Button>
        </div>

        {/* Zone Type Filters */}
        <div className="space-y-2">
          {ZONE_TYPES.map((zoneType) => {
            const Icon = zoneType.icon;
            const isSelected = zoneTypeFilter.includes(zoneType.type);
            
            return (
              <div
                key={zoneType.type}
                className={`flex items-center space-x-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                  isSelected 
                    ? `${zoneType.bgColor} ${zoneType.borderColor}` 
                    : "hover:bg-muted/50"
                }`}
                onClick={() => handleTypeToggle(zoneType.type)}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleTypeToggle(zoneType.type)}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <Icon className={`h-4 w-4 ${zoneType.color}`} />
                <span className="text-sm font-medium flex-1">
                  {zoneType.label}
                </span>
                <Badge 
                  variant="secondary" 
                  className={`text-xs ${
                    isSelected 
                      ? `${zoneType.bgColor} ${zoneType.color} ${zoneType.borderColor}` 
                      : ""
                  }`}
                >
                  {zoneType.type}
                </Badge>
              </div>
            );
          })}
        </div>

        {/* Altitude Range Filter */}
        <div className="space-y-3 pt-2 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Operating Altitude Range (ft)</h4>
            {hasActiveAltitudeFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAltitude}
                className="h-6 px-2 text-xs"
              >
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
          
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Show zones that overlap with this altitude range
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="min-altitude" className="text-xs">Min (ft)</Label>
                <Input
                  id="min-altitude"
                  type="number"
                  placeholder="Min ft"
                  value={minAltitude ?? ""}
                  onChange={(e) => {
                    const value = e.target.value === "" ? null : parseFloat(e.target.value);
                    setMinAltitude(value);
                    // If user enters a value, ensure restrictions are visible
                    if (value !== null && !shouldShowFlightRestrictions) {
                      setShouldShowFlightRestrictions(true);
                    }
                  }}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="max-altitude" className="text-xs">Max (ft)</Label>
                <Input
                  id="max-altitude"
                  type="number"
                  placeholder="Max ft"
                  value={maxAltitude ?? ""}
                  onChange={(e) => {
                    const value = e.target.value === "" ? null : parseFloat(e.target.value);
                    setMaxAltitude(value);
                    // If user enters a value, ensure restrictions are visible
                    if (value !== null && !shouldShowFlightRestrictions) {
                      setShouldShowFlightRestrictions(true);
                    }
                  }}
                  className="h-8 text-xs"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {selectedCount === 0 && (
          <div className="text-center py-4 text-muted-foreground text-sm">
            <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No zones selected</p>
            <p className="text-xs">All zones will be hidden</p>
          </div>
        )}

        {selectedCount > 0 && selectedCount < totalCount && (
          <div className="text-center py-2 text-muted-foreground text-xs">
            Showing {selectedCount} of {totalCount} zone types
          </div>
        )}
          </div>
        </>
      )}
    </Card>
  );
};
